'use client';
import React from 'react';
import Image from 'next/image';
import Link from 'next/link';

export default function Footer() {
  const currentYear = 2025;

  const footerLinks = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Our Team', href: '#team' },
    { label: 'Contact', href: '#contact' },
  ];

  const services = [
    { label: 'Commercial Real Estate Finance', href: '/services/commercial-real-estate-finance' },
    { label: 'Asset-Based Lending', href: '/services/asset-based-lending' },
    { label: 'Financial Advisory Services', href: '/services/financial-advisory' },
    { label: 'Business Loan Management', href: '/services/business-loan-management' },
  ];

  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="w-full bg-brand-darkbg border-t border-white/10">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="relative w-14 h-14 flex-shrink-0">
                <Image
                  src="/assets/images/logo-1786968063771.png"
                  alt="Grid Global Commercial Brokerage LLC Logo"
                  fill
                  className="object-contain"
                />
              </div>
              <div>
                <div className="font-serif font-bold text-white text-base leading-tight">Grid Global</div>
                <div className="font-sans text-brand-lightblue text-xs">Commercial Brokerage LLC</div>
              </div>
            </div>
            <p className="font-sans text-gray-400 text-sm leading-relaxed mb-6">
              A premier commercial brokerage and financial services firm delivering exceptional results through expert loan management and strategic financial consulting.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-3">
              {[
                {
                  name: 'LinkedIn',
                  icon: (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  ),
                },
                {
                  name: 'Facebook',
                  icon: (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  ),
                },
                {
                  name: 'Twitter',
                  icon: (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  ),
                },
              ].map((social) => (
                <a
                  key={social.name}
                  href="#"
                  aria-label={social.name}
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-brand-blue flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200 hover:scale-110"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-serif text-lg font-semibold text-white mb-5">Quick Links</h3>
            <nav>
              <ul className="space-y-2.5">
                {footerLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                      className="font-sans text-gray-400 text-sm hover:text-brand-green transition-colors flex items-center gap-2 group"
                    >
                      <span className="w-0 group-hover:w-3 h-px bg-brand-green transition-all duration-200 overflow-hidden" />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-serif text-lg font-semibold text-white mb-5">Our Services</h3>
            <ul className="space-y-2.5">
              {services.map((service) => (
                <li key={service.label}>
                  <Link
                    href={service.href}
                    className="font-sans text-gray-400 text-sm hover:text-brand-green transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-brand-green transition-all duration-200 overflow-hidden" />
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-serif text-lg font-semibold text-white mb-5">Address</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <svg className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="font-sans text-gray-400 text-sm leading-relaxed">
                  <strong className="text-gray-300">Head office:</strong><br />
                  170-01 Sit Tower Silicon Oasis<br />
                  Dubai, UAE<br /><br />
                  <strong className="text-gray-300">Office:</strong><br />
                  Ali Al-Thani Building 25,<br />
                  Street 908, Zone 38<br />
                  Doha, Qatar
                </p>
              </div>
              <div className="flex gap-3">
                <svg className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <p className="font-sans text-gray-400 text-sm">+974 6674 9254</p>
              </div>
              <div className="flex gap-3">
                <svg className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="font-sans text-gray-400 text-sm">customerservice@gridglobalcommericialbrokeragellc.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-sans text-gray-500 text-sm text-center sm:text-left">
            Copyright &copy; {currentYear} | Grid Global Commercial Brokerage LLC. All Rights Reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="font-sans text-gray-500 text-xs hover:text-gray-300 transition-colors">Privacy Policy</a>
            <span className="text-gray-600">|</span>
            <a href="#" className="font-sans text-gray-500 text-xs hover:text-gray-300 transition-colors">Terms of Service</a>
            <span className="text-gray-600">|</span>
            <a href="#" className="font-sans text-gray-500 text-xs hover:text-gray-300 transition-colors">Disclaimer</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
